import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// post-client-mod -- "我的文章" 面板（Slot 内容主体）
//
// 通过 user-center-client-mod 的 Slot 系统注入到用户中心页面。
// ============================================================================
import * as React from 'react';
import { usePosts } from '../hooks/use-posts.js';
import { useDeletePost } from '../hooks/use-delete-post.js';
import { PostListItem } from './post-list-item.js';
import { PostEditor } from './post-editor.js';
import { CategoryManager } from './category-manager.js';
import { TagManager } from './tag-manager.js';
import { PlusIcon, EmptyIcon } from './icons.js';
const TABS = [
    { id: 'all', label: '全部' },
    { id: 'published', label: '已发布' },
    { id: 'draft', label: '草稿' },
    { id: 'archived', label: '已归档' },
];
const MANAGE_TABS = [
    { id: 'posts', label: '文章' },
    { id: 'categories', label: '分类' },
    { id: 'tags', label: '标签' },
];
/**
 * "我的文章" 面板
 *
 * Slot 内容组件，展示当前用户的文章列表，支持 CRUD 操作。
 */
export function PostPanel() {
    const [activeTab, setActiveTab] = React.useState('all');
    const [manageTab, setManageTab] = React.useState('posts');
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [editingPost, setEditingPost] = React.useState(null);
    const [deleteConfirm, setDeleteConfirm] = React.useState(null);
    // 根据当前 tab 决定过滤参数
    const filterStatus = activeTab === 'all' ? undefined : activeTab;
    const { posts, pagination, loading, error, refresh, setPage } = usePosts({ status: filterStatus, pageSize: 5 });
    const { deleting, deletePost } = useDeletePost();
    // 打开新建编辑器
    const handleCreate = () => {
        setEditingPost(null);
        setEditorOpen(true);
    };
    // 打开编辑编辑器
    const handleEdit = (post) => {
        setEditingPost(post);
        setEditorOpen(true);
    };
    // 关闭编辑器
    const handleEditorClose = () => {
        setEditorOpen(false);
        setEditingPost(null);
    };
    // 编辑成功
    const handleEditorSuccess = () => {
        setEditorOpen(false);
        setEditingPost(null);
        refresh();
    };
    // 确认删除
    const handleDeleteConfirm = async () => {
        if (!deleteConfirm)
            return;
        const success = await deletePost(deleteConfirm.id);
        if (success) {
            setDeleteConfirm(null);
            refresh();
        }
    };
    return (_jsxs("div", { style: styles.panel, children: [_jsxs("div", { style: styles.header, children: [_jsx("h2", { style: styles.title, children: "\u6211\u7684\u6587\u7AE0" }), manageTab === 'posts' && !editorOpen && (_jsxs("button", { onClick: handleCreate, style: styles.createButton, children: [_jsx(PlusIcon, {}), _jsx("span", { style: { marginLeft: '6px' }, children: "\u65B0\u5EFA\u6587\u7AE0" })] }))] }), _jsx("div", { style: styles.manageTabBar, children: MANAGE_TABS.map(tab => (_jsx("button", { onClick: () => setManageTab(tab.id), style: {
                        ...styles.manageTab,
                        ...(manageTab === tab.id ? styles.manageTabActive : {}),
                    }, children: tab.label }, tab.id))) }), manageTab === 'posts' && (_jsxs(_Fragment, { children: [editorOpen && (_jsxs("div", { style: styles.editorContainer, children: [_jsx("h3", { style: styles.editorTitle, children: editingPost ? '编辑文章' : '新建文章' }), _jsx(PostEditor, { post: editingPost ?? undefined, onSuccess: handleEditorSuccess, onCancel: handleEditorClose })] })), !editorOpen && (_jsx("div", { style: styles.tabBar, children: TABS.map(tab => (_jsx("button", { onClick: () => setActiveTab(tab.id), style: {
                                ...styles.tab,
                                ...(activeTab === tab.id ? styles.tabActive : {}),
                            }, children: tab.label }, tab.id))) })), !editorOpen && (_jsxs("div", { style: styles.listContainer, children: [!loading && !error && posts.length === 0 && (_jsxs("div", { style: styles.empty, children: [_jsx(EmptyIcon, {}), _jsx("p", { style: styles.emptyText, children: "\u8FD8\u6CA1\u6709\u6587\u7AE0" }), _jsx("p", { style: styles.emptySubtext, children: "\u70B9\u51FB\u4E0A\u65B9\u6309\u94AE\u521B\u5EFA\u7B2C\u4E00\u7BC7\u6587\u7AE0" })] })), loading && _jsx("div", { style: styles.loading, children: "\u52A0\u8F7D\u4E2D..." }), error && _jsx("div", { style: styles.error, children: error }), !loading &&
                                !error &&
                                posts.map(post => (_jsx(PostListItem, { post: post, onEdit: handleEdit, onDelete: (p) => setDeleteConfirm(p) }, post.id))), !loading && pagination && pagination.totalPages > 1 && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { onClick: () => setPage(Math.max(1, pagination.page - 1)), disabled: pagination.page <= 1, style: styles.pageButton, children: "\u4E0A\u4E00\u9875" }), _jsxs("span", { style: styles.pageInfo, children: [pagination.page, " / ", pagination.totalPages] }), _jsx("button", { onClick: () => setPage(Math.min(pagination.totalPages, pagination.page + 1)), disabled: pagination.page >= pagination.totalPages, style: styles.pageButton, children: "\u4E0B\u4E00\u9875" })] }))] }))] })), manageTab === 'categories' && (_jsx(CategoryManager, { onUpdated: () => refresh() })), manageTab === 'tags' && (_jsx(TagManager, { onUpdated: () => refresh() })), deleteConfirm && (_jsx("div", { style: styles.modalOverlay, onClick: () => setDeleteConfirm(null), children: _jsxs("div", { style: styles.modal, onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { style: styles.modalTitle, children: "\u786E\u8BA4\u5220\u9664" }), _jsxs("p", { style: styles.modalText, children: ["\u786E\u5B9A\u8981\u5220\u9664\u6587\u7AE0\u300C", deleteConfirm.title, "\u300D\u5417\uFF1F\u5220\u9664\u540E\u65E0\u6CD5\u6062\u590D\u3002"] }), _jsxs("div", { style: styles.modalActions, children: [_jsx("button", { onClick: () => setDeleteConfirm(null), disabled: deleting, style: styles.cancelButton, children: "\u53D6\u6D88" }), _jsx("button", { onClick: handleDeleteConfirm, disabled: deleting, style: styles.deleteButton, children: deleting ? '删除中...' : '确认删除' })] })] }) }))] }));
}
const styles = {
    panel: {
        maxWidth: '720px',
        margin: '0 auto',
        padding: '32px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
    },
    title: {
        margin: 0,
        fontSize: '24px',
        fontWeight: 700,
        color: '#1e293b',
    },
    createButton: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 16px',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    manageTabBar: {
        display: 'flex',
        gap: '4px',
        marginBottom: '16px',
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: '8px',
    },
    manageTab: {
        padding: '6px 14px',
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: '13px',
        fontWeight: 500,
        color: '#6b7280',
        cursor: 'pointer',
        borderRadius: '6px',
        transition: 'all 0.15s ease',
    },
    manageTabActive: {
        backgroundColor: '#eef2ff',
        color: '#4338ca',
    },
    tabBar: {
        display: 'flex',
        gap: '4px',
        marginBottom: '16px',
        borderBottom: '1px solid #e5e7eb',
    },
    tab: {
        padding: '8px 16px',
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: '14px',
        fontWeight: 500,
        color: '#6b7280',
        cursor: 'pointer',
        borderBottom: '2px solid transparent',
        marginBottom: '-1px',
    },
    tabActive: {
        color: '#6366f1',
        borderBottomColor: '#6366f1',
    },
    listContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    editorContainer: {
        padding: '24px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '16px',
    },
    editorTitle: {
        margin: '0 0 16px 0',
        fontSize: '18px',
        fontWeight: 600,
        color: '#1e293b',
    },
    loading: {
        padding: '40px',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '14px',
    },
    error: {
        padding: '16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        color: '#dc2626',
        fontSize: '14px',
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        gap: '12px',
    },
    emptyText: {
        margin: 0,
        fontSize: '16px',
        fontWeight: 500,
        color: '#4b5563',
    },
    emptySubtext: {
        margin: 0,
        fontSize: '13px',
        color: '#9ca3af',
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '24px',
    },
    pageButton: {
        padding: '6px 12px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        fontSize: '13px',
        cursor: 'pointer',
    },
    pageInfo: {
        fontSize: '13px',
        color: '#6b7280',
    },
    modalOverlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        width: '400px',
        maxWidth: '90vw',
    },
    modalTitle: {
        margin: '0 0 12px 0',
        fontSize: '18px',
        fontWeight: 600,
        color: '#1e293b',
    },
    modalText: {
        margin: '0 0 20px 0',
        fontSize: '14px',
        color: '#4b5563',
        lineHeight: '22px',
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
    },
    cancelButton: {
        padding: '8px 16px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        fontSize: '14px',
        cursor: 'pointer',
    },
    deleteButton: {
        padding: '8px 16px',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#ef4444',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
};
//# sourceMappingURL=post-panel.js.map